import { tokens } from "lib/colors"
import { gtmEvent } from "lib/gtm"
import { colorType } from "lib/interfaces"
import Icons from "lib/components/Icons"
import Text, { weightProps } from "lib/components/Text"
import Link from "next/link"
import { RefObject, useContext, useEffect, useMemo, useRef, useState } from "react"
import GhostContentAPI from "@tryghost/content-api"
import { LanguageContext } from "context/LanguageContext"

type ProgramCategories = {
  id: any
  category_name: string
  slug?: string
}

type CategoriesQuery = {
  parent_id: any
  slug?: string
  priority: number
  category_name: string
  category_description?: string
  id: any

  categories: ProgramCategories[]
}

interface CloseDropdownFunction {
  elementRef: RefObject<HTMLDivElement>
  isDropdownOpen: boolean
}

export type keySelectCategory = "category_name" | "category_name_indo"
export interface CategorySelectorProps {
  color?: colorType
  weight?: weightProps
  isHeader?: boolean
  categories?: CategoriesQuery[]
  language: "ID" | "EN"
  title?: string
  topCategories?: string
  allCategory?: string
  setActiveDropdown
  activeDropdown
  showMarquee?: boolean
}

const api = new GhostContentAPI({
  url: process.env.NEXT_PUBLIC_GHOST_HOST,
  key: process.env.NEXT_PUBLIC_GHOST_KEY,
  version: "v3",
})

function BlogDropdownCollaboration({
  isHeader = false,
  color = "light",
  weight = "normal",
  categories = [],
  language = "ID",
  title = "Training",
  topCategories = "Top Categories",
  allCategory = "All Categories",
  setActiveDropdown,
  activeDropdown,
  showMarquee,
}: CategorySelectorProps) {
  const [keySelectedCategory, setKeySelectedCategory] = useState<keySelectCategory>("category_name")
  const categoryData = [
    {
      slug: "education",
      category_name: "Education",
      category_name_indo: "Pendidikan",
    },
    {
      slug: "institution",
      category_name: "Institution",
      category_name_indo: "Institusi",
    },
    {
      slug: "community",
      category_name: "Community",
      category_name_indo: "Komunitas",
    },
  ]

  const refCategory: any = useRef()
  let animationType = "opacity-0"
  let animationIcon = "transform rotate-0 duration-200"
  let hexColor = isHeader ? tokens.neutral[10] : "#000"

  if (activeDropdown === "collaboration") {
    animationType = "opacity-100"
    animationIcon = "transform rotate-180 duration-200"
  }

  if (activeDropdown !== "collaboration") {
    animationType = "hidden"
  }

  switch (color) {
    case "primary":
      hexColor = tokens.primary.DEFAULT
      break
    case "secondary":
      hexColor = tokens.secondary.DEFAULT
      break
    default:
      break
  }

  useMemo(() => {
    setKeySelectedCategory("category_name")
    if (language === "ID") {
      setKeySelectedCategory("category_name_indo")
    }
  }, [language])

  const closeDropdown = ({ elementRef, isDropdownOpen }: CloseDropdownFunction) => {
    // close if user click outside the dropdown and isDropdownOpen true
    if (!elementRef.current?.matches(":hover") && isDropdownOpen) {
      setActiveDropdown(null)
    }
  }

  return (
    <div className="relative">
      <div
        role="button"
        tabIndex={0}
        className={`flex flex-row gap-0 items-center ${isHeader ? "border-b-2 border-primary" : "border-b-2 border-transparent"} py-1.5 sm:py-3 sm:my-0`}
        onClick={() => {
          if (activeDropdown === "collaboration") {
            setActiveDropdown(null)
          } else {
            setActiveDropdown("collaboration")
          }
        }}
        onBlur={() =>
          closeDropdown({
            elementRef: refCategory,
            isDropdownOpen: activeDropdown === "collaboration",
          })
        }>
        <Text weight={weight} customClass={`font-semibold ${isHeader ? "!text-primary" : "!text-neutral-50"} text-sm sm:text-base`}>
          {title}
        </Text>
        <Icons iconName="expand-more" styles={animationIcon} color={isHeader ? tokens.primary.DEFAULT : tokens.neutral[50]} />
      </div>
      <div
        ref={refCategory}
        className={`transform ease-in-out duration-500 ${animationType} absolute mx-auto left-0 sm:-left-4 top-12 lg:top-8 mt-2 sm:mt-6 z-20 ${activeDropdown === "collaboration" ? "hover:block" : "hidden"}`}
        onBlur={() =>
          closeDropdown({
            elementRef: refCategory,
            isDropdownOpen: activeDropdown === "collaboration",
          })
        }>
        <div className="bg-white rounded-lg shadow-search flex flex-row h-[200px] w-[200px]">
          <div className="w-full px-2 py-2 overflow-x-auto flex flex-col gap-y-2">
            <Link href="/blog?type=collaboration" passHref legacyBehavior>
              <div
                key="all"
                className="px-1.5 hover:px-2 py-2 rounded-md hover:bg-info-surface !text-neutral-50 hover:!text-black cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => {
                  gtmEvent("ce_header_menu_click", {
                    menu_name: "All Categories",
                  })
                }}>
                <Text>{allCategory}</Text>
              </div>
            </Link>
            {categoryData.map((category) => (
              <Link
                key={category.slug}
                href={`/blog?type=collaboration&tag=${category.slug}`}
                passHref
                legacyBehavior>
                <div
                  key="all"
                  className="px-1.5 hover:px-2 py-2 rounded-md hover:bg-info-surface !text-neutral-50 hover:!text-black cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    gtmEvent("ce_header_menu_click", {
                      menu_name: category.category_name,
                    })
                    setActiveDropdown(null)
                  }}>
                  <Text>
                    {category[keySelectedCategory] || category.category_name}
                  </Text>
                </div>
                </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BlogDropdownCollaboration
